import React, { useCallback, useEffect } from 'react';
import { useTranslatedContent } from '../../../../hooks/useTranslatedContent';
import styles from './MenuCategories.module.less';
import { useCategories } from '../../../../hooks/useCategories';
import { Category } from '../../../../types/categories';
import { useMenuStateStore, useMenuStore } from '../../../../stores/menuStore';
import { Icon } from '../../../shared/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useFilters } from '../../../../hooks/useFilters';
import useIsMobile from '../../../../hooks/useIsMobile';

const MenuCategories: React.FC = () => {
  const { data: categoriesData, isError, error } = useCategories();
  const { getTranslation } = useTranslatedContent();
  const { setSelectedCategory } = useFilters();
  const [openAccordions, setOpenAccordions] = React.useState<string[]>([]);
  const {
    currentStep,
    tempSelectedCategory,
    tempSelectedSubcategories,
    setTempCategory,
    setTempSubcategories,
    setCurrentStep,
  } = useMenuStore();

  const { closeMenu } = useMenuStateStore();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleAboutFund = () => {
    navigate('/about-fund');
    closeMenu();
  };

  useEffect(() => {
    if (tempSelectedCategory && window.innerWidth >= 768) {
      setOpenAccordions([tempSelectedCategory]);
    }
  }, [tempSelectedCategory]);

 
  const handleProgramClick = useCallback(
    (slug: string, categorySlug: string, id: string) => {
      setSelectedCategory(id);
      navigate(`/about_project/${slug}/${categorySlug}`);
      closeMenu();
    },
    [navigate, setSelectedCategory, closeMenu]
  );

  const getCategorySubcategories = useCallback((categoryId: string) => {
    if (!categoriesData) return [];
    return categoriesData.subcategories.filter((subcategory) =>
      subcategory.categories.some((cat) => cat.categories_id === categoryId)
    );
  }, [categoriesData]);

  const handleCategoryClick = useCallback((categoryId: string) => {
    setTempCategory(categoryId);
    if (window.innerWidth < 768) {
      setCurrentStep('subcategories');
    } else {
      setOpenAccordions((prev) =>
        prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId]
      );
    }
  }, [setTempCategory, setCurrentStep]);

  const handleSubcategoryClick = useCallback((subcategoryId: string) => {
    if (tempSelectedSubcategories.includes(subcategoryId)) {
      setTempSubcategories(
        tempSelectedSubcategories.filter((id) => id !== subcategoryId)
      );
    } else {
      setTempSubcategories([...tempSelectedSubcategories, subcategoryId]);
    }
  }, [tempSelectedSubcategories, setTempSubcategories]);


  if (isError) {
    return <div>Error loading categories: {(error as Error).message}</div>;
  }

  if (!categoriesData) {
    return null;
  }

  const renderMobileCategories = () => (
    <div className={styles.menuCategories__listWrapper}>
      <ul className={styles.menuCategories__list}>
        {categoriesData.categories.map((category: Category) => (
          <li key={category.id} className={styles.menuCategories__itemWrapper}>
            <button
              className={`${styles.menuCategories__item} ${
                tempSelectedCategory === category.id
                  ? styles.menuCategories__item_active
                  : ''
              }`}
              onClick={() => handleCategoryClick(category.id)}
            >
              <span>{getTranslation(category, 'category_name')}</span>
            </button>
            <Icon id="arrow-right" className={styles.menuCategories__arrow} />
          </li>
        ))}
      </ul>
      <ul className={styles.menuCategories__sectionLinkToPageWrapper}>
        <li
          className={styles.menuCategories__sectionLinkToPage}
          onClick={handleAboutFund}
        >
          <div className={`${styles.menuCategories__itemLink}`}>
            {t('About the direction')}
          </div>
          <Icon id="arrow" className={styles.iconArrow} />
        </li>
        <Link
          to="/about-fund#contacts"
          onClick={closeMenu}
          className={`${styles.menuCategories__itemLink}`}
        >
          <li className={styles.menuCategories__sectionLinkToPage}>
            <p>{t('Contacts')}</p>
            <Icon id="arrow" className={styles.iconArrow} />
          </li>
        </Link>
      </ul>
    </div>
  );

  const renderMobileSubcategories = () => {
    const selectedCategory = categoriesData.categories.find(
      (cat) => cat.id === tempSelectedCategory
    );

    if (!selectedCategory) return null;

    const subcategories = getCategorySubcategories(selectedCategory.id);

    return (
      <div className={styles.menuCategories__listWrapper}>
        <ul className={styles.menuCategories__subcategoriesList}>
          {subcategories.map((subcategory) => (
            <li
              key={subcategory.id}
              className={styles.menuCategories__subitemWrapper}
              onClick={() => handleSubcategoryClick(subcategory.id)}
            >
              <button
                className={`${styles.menuCategories__subitem} ${
                  tempSelectedSubcategories.includes(subcategory.id)
                    ? styles.menuCategories__subitem_active
                    : ''
                }`}
              >
                <span>{getTranslation(subcategory, 'subcategory_name')}</span>
                {tempSelectedSubcategories.includes(subcategory.id) && (
                  <Icon id="close-dropdown" className={styles.iconCross} />
                )}
              </button>
            </li>
          ))}
        </ul>
        {selectedCategory.details?.[0]?.categories_details_id?.category_page.length > 0 && (
          <li
            className={`${styles.menuCategories__subitemWrapper} ${styles.menuCategories__subitemWrapper__link}`}
            onClick={() =>
              handleProgramClick(
                selectedCategory.slug,
                selectedCategory.details[0].categories_details_id?.category_page[0].about_project_page_id.slug,
                selectedCategory?.id
              )
            }
          >
            <button className={styles.menuCategories__subitem}>
              {t('About the direction')}
            </button>
            <Icon id="arrow" className={styles.iconArrow} />
          </li>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <div className={styles.menuCategories__mobile}>
          {currentStep === 'categories'
            ? renderMobileCategories()
            : renderMobileSubcategories()}
        </div>
      ) : (
        <div className={styles.menuCategories__listWrapperDesctop}>
          <ul className={styles.menuCategories__desktop}>
            {categoriesData.categories.map((category: Category) => (
              <li key={category.id} className={styles.menuCategories__section}>
                <div
                  className={styles.menuCategories__categoryDesktopWrapper}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <button
                    className={`${styles.menuCategories__categoryDesktop} ${
                      openAccordions.includes(category.id)
                        ? styles.menuCategories__header_active
                        : ''
                    }`}
                  >
                    {getTranslation(category, 'category_name')}
                  </button>
                  <Icon
                    id={openAccordions.includes(category.id) ? 'arrow-up' : 'arrow-right'}
                    className={styles.menuCategories__arrow}
                  />
                </div>
                {openAccordions.includes(category.id) && (
                  <ul className={styles.menuCategories__subcategories}>
                    {getCategorySubcategories(category.id).map((subcategory) => (
                      <li
                        key={subcategory.id}
                        className={styles.menuCategories__subitemWrapper}
                        onClick={() => handleSubcategoryClick(subcategory.id)}
                      >
                        <button
                          className={`${styles.menuCategories__subitem} ${
                            tempSelectedSubcategories.includes(subcategory.id)
                              ? styles.menuCategories__subitem_active
                              : ''
                          }`}
                        >
                          {getTranslation(subcategory, 'subcategory_name')}
                          {tempSelectedSubcategories.includes(subcategory.id) && (
                            <Icon id="close-dropdown" className={styles.iconCross} />
                          )}
                        </button>
                      </li>
                    ))}
                    {category.details?.[0]?.categories_details_id?.category_page.length > 0 && (
                      <li
                        className={`${styles.menuCategories__subitemWrapper} ${styles.menuCategories__subitemWrapper}`}
                        onClick={() =>
                          handleProgramClick(
                            category?.slug,
                            category?.details[0].categories_details_id?.category_page[0].about_project_page_id.slug,
                            category?.id
                          )
                        }
                      >
                        <button className={styles.menuCategories__subitem}>
                          {t('About the direction')}
                        </button>
                        <Icon id="arrow" className={styles.iconArrow} />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <ul className={styles.menuCategories__sectionLinkToPageWrapper}>
            <li
              className={styles.menuCategories__sectionLinkToPage}
              onClick={handleAboutFund}
            >
              <div className={`${styles.menuCategories__categoryDesktop}`}>
                {t('About MethodFund')}
              </div>
              <Icon id="arrow" className={styles.iconArrow} />
            </li>
            <Link
              to="/about-fund#contacts"
              onClick={closeMenu}
              className={`${styles.menuCategories__categoryDesktop}`}
            >
              <li className={styles.menuCategories__sectionLinkToPage}>
                <p>{t('Contacts')}</p>
                <Icon id="arrow" className={styles.iconArrow} />
              </li>
            </Link>
          </ul>
        </div>
      )}
    </>
  );
};

export default MenuCategories;